import { getAccessToken } from "../auth";
import { getConfig } from "../config";
import type { TranscriptEntry, LabelFile } from "../types";

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await getAccessToken();
  const base = getConfig().apiUrl.replace(/\/$/, "");
  const res = await fetch(`${base}${path}`, {
    ...init,
    headers: {
      ...(init.headers || {}),
      Authorization: token,
      "Content-Type": "application/json",
    },
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`${res.status} ${res.statusText}: ${body}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export async function listTranscripts(): Promise<TranscriptEntry[]> {
  const data = await request<{ items: Array<{ fileId: string; displayName: string; state: string }> }>("/transcripts");
  return data.items.map((item) => ({
    fileId: item.fileId,
    state: item.state as TranscriptEntry["state"],
  }));
}

export async function getTranscript(fileId: string): Promise<string> {
  const data = await request<{ fileId: string; markdown: string }>(
    `/transcripts/${encodeURIComponent(fileId)}`,
  );
  return data.markdown;
}

export interface LabelConfig {
  active: string[];
  archived: string[];
}

export async function getLabels(): Promise<LabelConfig> {
  const data = await request<LabelConfig>("/labels");
  return { active: data.active ?? [], archived: data.archived ?? [] };
}

export async function addLabel(name: string): Promise<LabelConfig> {
  return request<LabelConfig>("/labels", {
    method: "POST",
    body: JSON.stringify({ name }),
  });
}

export async function renameLabel(oldName: string, newName: string): Promise<LabelConfig> {
  return request<LabelConfig>(`/labels/${encodeURIComponent(oldName)}`, {
    method: "PUT",
    body: JSON.stringify({ newName }),
  });
}

export async function archiveLabel(name: string): Promise<LabelConfig> {
  return request<LabelConfig>(`/labels/${encodeURIComponent(name)}`, {
    method: "DELETE",
  });
}

export async function restoreLabel(name: string): Promise<LabelConfig> {
  return request<LabelConfig>(`/labels/${encodeURIComponent(name)}/restore`, {
    method: "POST",
  });
}

export async function getLabelFile(fileId: string): Promise<LabelFile | null> {
  const data = await request<LabelFile | Record<string, never>>(
    `/transcripts/${encodeURIComponent(fileId)}/labelfile`,
  );
  if (!data || !("selections" in data)) return null;
  return data as LabelFile;
}

export async function saveDraft(fileId: string, data: LabelFile): Promise<void> {
  await request(`/transcripts/${encodeURIComponent(fileId)}/labelfile/draft`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function saveCompleted(fileId: string, data: LabelFile): Promise<void> {
  await request(`/transcripts/${encodeURIComponent(fileId)}/labelfile/completed`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deleteDraft(fileId: string): Promise<void> {
  await request(`/transcripts/${encodeURIComponent(fileId)}/labelfile/draft`, {
    method: "DELETE",
  });
}
